import { Env } from '@env';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { format } from 'date-fns';
import { Image } from 'expo-image';
import { useFocusEffect } from 'expo-router';
import { t } from 'i18next';
import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { Dimensions, Linking, Pressable, ScrollView, View } from 'react-native';
import Modal from 'react-native-modal';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import { useCustomer } from '@/api/customers/use-customer';
import type { Subscription } from '@/api/subscriptions/types';
import { useSubscription } from '@/api/subscriptions/use-subscription';
import {
  type CreateSubResponse,
  useSubscriptionCreate,
} from '@/api/subscriptions/use-subscription-create';
import {
  getCheapestModelKey,
  useSubscriptionOptions,
} from '@/api/subscriptions/use-subscription-options';
import { useSubscriptionUpdateSignature } from '@/api/subscriptions/use-subscription-update-signature';
import { Canvas } from '@/components/canvas';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import {
  dataURLtoFile,
  formatNumber,
  getSubscriptionBackgroundImageUri,
  getSubscriptionInsuranceCost,
  isDesktop,
  rgbaToHex,
  snakeToTitleCase,
  titleize,
  useCurrentOrganization,
} from '@/core';
import mixpanelService from '@/core/clients/mixpanel';
import useSubscriptionStore from '@/stores/use-subscription-store';
import { PINKLING } from '@/style/colors';
import { Button, Card, showErrorMessage, Text } from '@/ui';
import { Dialog } from '@/ui/dialog';
import { Icon } from '@/ui/icon';
import { Bolt } from '@/ui/icons/bolt';
import { ChevronRight } from '@/ui/icons/chevron-right';
import { ClockBadgeCheckmark } from '@/ui/icons/clock-badge-checkmark';
import { Dollar } from '@/ui/icons/dollar';
import { Lock } from '@/ui/icons/lock';
import { Loader } from '@/ui/loader';
import { Tooltip } from '@/ui/tooltip';

const PAGE_HEIGHT = Dimensions.get('window').height;

const stripePromise = loadStripe(Env.STRIPE_PUBLISHABLE_KEY);

const StripeWrapper = forwardRef((props, ref) => {
  const { theme } = useStyles(stylesheet);

  const stripeSecret = useSubscriptionStore((state) => state.stripeSecret);
  const setStripeSecret = useSubscriptionStore(
    (state) => state.setStripeSecret
  );
  const customerSessionSecret = useSubscriptionStore(
    (state) => state.customerSessionSecret
  );
  const setCustomerSessionSecret = useSubscriptionStore(
    (state) => state.setCustomerSessionSecret
  );
  const setStripeCustomerId = useSubscriptionStore(
    (state) => state.setStripeCustomerId
  );

  const address = useSubscriptionStore((state) => state.address);
  const startDatetime = useSubscriptionStore((state) => state.startDatetime);
  const period = useSubscriptionStore((state) => state.period);
  const car = useSubscriptionStore((state) => state.car);
  const autopilot = useSubscriptionStore((state) => state.autopilot);
  const mileage = useSubscriptionStore((state) => state.mileage);
  const insurance = useSubscriptionStore((state) => state.insurance);
  const discount = useSubscriptionStore((state) => state.discount);

  const [subscriptionId, setSubscriptionId] = React.useState<number | null>(
    null
  );
  const [subscription, setSubscription] = useState<Subscription | null>(null);

  const { mutate: createSub, isPending: isCreatingSub } =
    useSubscriptionCreate();

  const { data: subData, isPending: isFetchingSub } = useSubscription({
    //@ts-ignore
    variables: { id: subscriptionId },
    enabled: !!subscriptionId,
  });

  const { data: customer } = useCustomer();

  useFocusEffect(
    React.useCallback(() => {
      if (!car || !insurance || !startDatetime || !period) {
        return;
      }

      createSub(
        {
          model: car!.model.name,
          trim: `${car!.model.name}_${car!.trim.name}`,
          address: address!.description,
          customer: customer!,
          renewalPeriod: period === '6Months' ? '6_months' : period,
          startTime: startDatetime!.toISOString(),
          autopilot: autopilot!,
          mileage: mileage!,
          insuranceId: insurance!.id,
          discount: discount!,
          customPickupLocation: period !== 'weekly' ? address!.description : '',
          customReturnLocation: period !== 'weekly' ? address!.description : '',
        },
        {
          onSuccess: (data: CreateSubResponse) => {
            setSubscription(data.subscription);
            setSubscriptionId(data.subscription.id);
            setStripeSecret(data.stripeSecret);
            setCustomerSessionSecret(data.customerSessionSecret);
            setStripeCustomerId(data.stripeCustomerId);
          },
          onError: (e) => {
            showErrorMessage(e.message);
          },
        }
      );
    }, [
      startDatetime,
      car,
      insurance,
      address,
      autopilot,
      mileage,
      period,
      customer,
      setStripeSecret,
      setCustomerSessionSecret,
      setStripeCustomerId,
    ])
  );

  if (!stripePromise || !stripeSecret || !customerSessionSecret || !subData) {
    return (
      <>
        {/* <Stack.Screen
          options={{
            headerTitle: 'Checkout',
            headerLeft: () => <BackButton />,
            headerTitleAlign: 'center',
            headerShadowVisible: false,
            headerStyle: {
              backgroundColor: theme.colors.background_1,
            },
            headerTintColor: theme.colors.text.primary,
          }}
        /> */}

        <View
          style={{
            width: '100%',
            height: '100%',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: theme.colors.background_1,
          }}
        >
          <Loader style={{ height: 250, width: 250 }} />
        </View>
      </>
    );
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        locale: 'en',
        clientSecret: stripeSecret,
        customerSessionClientSecret: customerSessionSecret,
        appearance: {
          variables: {
            colorBackground: rgbaToHex(theme.colors.background_2),
          },
          rules: {
            '.Tab': {
              border: 'none',
            },
          },
          theme: UnistylesRuntime.themeName === 'dark' ? 'night' : 'stripe',
        },
      }}
    >
      <Summary
        ref={ref}
        subscription={subData}
        onPaySuccess={props.onPaySuccess}
      />
    </Elements>
  );
});

const Summary = forwardRef<
  any,
  { subscription: Subscription; onPaySuccess: () => void }
>(({ subscription, onPaySuccess }, ref) => {
  const { styles, theme } = useStyles(stylesheet);

  const stripe = useStripe();
  const elements = useElements();

  const stripeSecret = useSubscriptionStore((state) => state.stripeSecret);
  const customerSessionSecret = useSubscriptionStore(
    (state) => state.customerSessionSecret
  );
  const [isFormValid, setIsFormValid] = React.useState(false);
  const [paymentMethodId, setPaymentMethodId] = React.useState<
    string | undefined
  >();
  const [signature, setSignature] = React.useState<string | undefined>();

  const address = useSubscriptionStore((state) => state.address);
  const startDatetime = useSubscriptionStore((state) => state.startDatetime);
  const period = useSubscriptionStore((state) => state.period);
  const brandName = useSubscriptionStore((state) => state.brandName);
  const modelName = useSubscriptionStore((state) => state.modelName);
  const trimName = useSubscriptionStore((state) => state.trimName);
  const autopilot = useSubscriptionStore((state) => state.autopilot);
  const mileage = useSubscriptionStore((state) => state.mileage);
  const insurance = useSubscriptionStore((state) => state.insurance);
  const discount = useSubscriptionStore((state) => state.discount);
  const car = useSubscriptionStore((state) => state.car);
  const mileageCost = useSubscriptionStore((state) => state.mileageCost);
  const [showPoliciesModal, setShowPoliciesModal] = React.useState(false);

  const paymentFormViewRef = React.useRef<View>(null);
  const scrollViewRef = React.useRef<ScrollView>(null);
  const signatureViewRef = React.useRef<View>(null);
  const [signatureError, setSignatureError] = React.useState<string | null>();
  const [stripePaymentPending, setStripePaymentPending] = React.useState(false);
  const { isEVFMC } = useCurrentOrganization();

  const { data: customer } = useCustomer();

  const { data: subscriptionOptions, isLoading } = useSubscriptionOptions({
    variables: {
      address: address?.description,
      startDate: startDatetime!,
    },
  });

  const { mutateAsync: updateSubSignature, isPending: isUpdatingSubSignature } =
    useSubscriptionUpdateSignature();

  const showMileageInBreakdown = useMemo(() => {
    if (isEVFMC) {
      return false;
    }

    return true;
  }, [isEVFMC]);

  const carCost = useMemo(() => {
    if (!subscriptionOptions) return 0;

    const selectedPeriod = period ? subscriptionOptions[period] : undefined;
    if (!selectedPeriod) return 0;

    let cost = 0;

    const cheapestModelKey = getCheapestModelKey(selectedPeriod);

    if (period && cheapestModelKey && !modelName) {
      cost = selectedPeriod[cheapestModelKey].cheapestOption.cost;
    }

    if (period && modelName && !trimName) {
      cost = selectedPeriod[`${brandName}${modelName}`].cheapestOption.cost;
    }

    if (period && modelName && trimName) {
      cost =
        selectedPeriod[`${brandName}${modelName}`].trimChoices[trimName][0]
          .cost;
    }

    return period === '6Months' ? cost / 6 : cost;
  }, [subscriptionOptions, period, modelName, trimName]);

  const deposit = useMemo(() => {
    if (!subscriptionOptions || !period) return 0;

    return subscriptionOptions[period]?.deposit ?? 0;
  }, [subscriptionOptions, period]);

  // Backend waives the initiation fee for EVFMC (always unlimited mileage in
  // this weekly flow) — see subscription_service.rb/organization_service.rb.
  const initiationFee = isEVFMC ? 0 : 99;

  const calculatedCost = useMemo(() => {
    let cost = carCost;

    if (autopilot) {
      cost += autopilot.amount;
    }

    if (mileage) {
      cost += mileageCost;
    }

    if (insurance) {
      cost += getSubscriptionInsuranceCost(insurance, carCost, period);
    }

    if (car && car.deliveryCost > 0) {
      cost += car.deliveryCost;
    }

    return cost;
  }, [autopilot, mileage, insurance, carCost, mileageCost, period]);

  const weeklyCostLabel = formatNumber(calculatedCost);
  const dailyCostLabel = formatNumber(calculatedCost / 7);
  const recurringTotal = calculatedCost - (car?.deliveryCost ?? 0);
  const periodUnit = period === 'weekly' ? 'week' : 'month';

  const getPeriodString = () => {
    if (period === 'weekly') return '7 days';
    if (period === 'monthly') return '30 days';
    if (period === '6Months') return '30 days';
  };

  const carAddress = useMemo(() => {
    if (!car) return '';

    return `${car.defaultLocation.address} ${
      car.defaultLocation.city
    }, ${snakeToTitleCase(car.defaultLocation.locationState)}, ${
      car.defaultLocation.zipcode
    }`;
  }, [car]);

  useImperativeHandle(ref, () => ({
    pay: () => {
      handleBooking();
    },
  }));

  const handleBooking = async () => {
    if (!isFormValid) {
      paymentFormViewRef.current?.measureLayout(
        scrollViewRef.current?.getInnerViewNode(),
        (x, y) => {
          scrollViewRef.current?.scrollTo({
            x: 0,
            y,
            animated: true,
          });
        }
      );
      return;
    }

    if (!signature) {
      setSignatureError('Please provide your signature');

      signatureViewRef.current?.measureLayout(
        scrollViewRef.current?.getInnerViewNode(),
        (x, y) => {
          scrollViewRef.current?.scrollTo({
            x: 0,
            y,
            animated: true,
          });
        }
      );

      return;
    }

    const contractSignatureImage = dataURLtoFile(
      signature,
      `contract_signature.png`
    );
    const contractInitialImage = dataURLtoFile(
      signature,
      `contract_initial.png`
    );

    await updateSubSignature({
      id: subscription.id,
      contractSignatureImage,
      contractInitialImage,
    });

    if (!stripe || !elements || !subscription) {
      // Stripe.js has not yet loaded.
      return;
    }

    setStripePaymentPending(true);

    const { error: submitError } = await elements.submit();
    if (submitError) {
      console.log('submit error', submitError);
      setStripePaymentPending(false);
      return;
    }

    let paymentMethod = paymentMethodId;

    let errorDuringBooking = null;
    if (paymentMethod !== '-1') {
      const { error } = await stripe.confirmCardPayment(stripeSecret, {
        payment_method: paymentMethod as string,
      });
      errorDuringBooking = error;
    } else if (paymentMethod === '-1') {
      const { error } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });
      errorDuringBooking = error;
    }

    setStripePaymentPending(false);

    if (errorDuringBooking) {
      mixpanelService.track('Stripe Payment Failed', {
        'Payment Error Code': errorDuringBooking.code,
        'Payment Error Message': errorDuringBooking.message,
      });

      showErrorMessage('Payment failed. Please try again');

      console.log('error', errorDuringBooking.message);
      return;
    } else {
      mixpanelService.track('Stripe Payment Succeeded');

      onPaySuccess();
    }
  };

  return (
    <View style={{ flex: 1 }}>
      {!isDesktop() && stripePaymentPending ? (
        <View
          style={{
            width: '100%',
            height: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme.colors.background_1,
          }}
        >
          <Loader style={{ height: 250, width: 250 }} />
        </View>
      ) : null}

      <ScrollView
        ref={scrollViewRef}
        style={styles.scrollViewContainer}
        contentContainerStyle={{
          flexGrow: 1,
          gap: 24,
        }}
        showsVerticalScrollIndicator={false}
      >
        <Text type="largeTitle">
          {customer?.firstname}, here is your subscription summary
        </Text>

        <Card style={{ paddingTop: 8 }}>
          <View style={{ padding: 16, gap: 16 }}>
            <View>
              <Image
                source={getSubscriptionBackgroundImageUri(
                  car?.brand?.name,
                  car?.model?.name
                )}
                contentFit="contain"
                style={styles.image}
              />
            </View>

            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Text type="title2" tx="subscriptions.breakdown" />
            </View>

            <View style={{ gap: 4 }}>
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                }}
              >
                <Text
                  tx={
                    period === 'weekly'
                      ? 'subscriptions.overviewLocation'
                      : 'subscriptions.overviewDeliveryLocation'
                  }
                  type="bodyRegular"
                />

                <Pressable
                  style={{ width: '60%' }}
                  onPress={() => {
                    const linkAddress =
                      period === 'weekly'
                        ? car?.defaultLocation.address
                        : address?.description;

                    if (linkAddress) {
                      const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                        linkAddress
                      )}`;
                      Linking.openURL(url).catch((err) =>
                        console.error('An error occurred', err)
                      );
                    }
                  }}
                >
                  <Text
                    type="bodyRegular"
                    style={{
                      color: theme.colors.text.secondary,
                      textAlign: 'right',
                      textDecorationLine:
                        (period === 'weekly'
                          ? carAddress
                          : address?.description) && 'underline',
                    }}
                  >
                    {(period === 'weekly'
                      ? carAddress
                      : address?.description) || 'Select a model first'}
                  </Text>
                </Pressable>
              </View>

              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                }}
              >
                <Text tx="subscriptions.overviewStartDate" type="bodyRegular" />

                <Text
                  type="bodyRegular"
                  style={{ color: theme.colors.text.secondary }}
                >
                  {format(startDatetime!, 'MMM dd yyyy')}
                </Text>
              </View>

              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                }}
              >
                <Text
                  type="caption1"
                  values={{
                    amount: formatNumber(calculatedCost),
                    period: getPeriodString(),
                  }}
                  style={{ color: theme.colors.text.secondary, maxWidth: 350 }}
                >
                  Your Rental agent will contact you 24 hours before your
                  subscription start time to coordinate a pickup time for you.
                </Text>
              </View>
            </View>
          </View>

          <View
            style={{
              backgroundColor: theme.colors.background_3,
              height: 2,
              width: '100%',
            }}
          />

          <View style={{ padding: 16, gap: 16 }}>
            <View style={{ gap: 4 }}>
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                }}
              >
                <Text
                  tx="subscriptions.overviewCommitment"
                  type="bodyRegular"
                  values={{
                    period: period === '6Months' ? 'Monthly' : titleize(period),
                  }}
                />

                <Text
                  type="bodyRegular"
                  style={{ color: theme.colors.text.secondary }}
                >
                  {`$${formatNumber(carCost)}`}
                </Text>
              </View>

              {brandName === 'tesla' ? (
                <View
                  style={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                  }}
                >
                  <Text type="bodyRegular">{autopilot?.title}</Text>

                  <Text
                    type="bodyRegular"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    {autopilot?.amount !== 0
                      ? `$${formatNumber(autopilot?.amount)}`
                      : 'Included'}
                  </Text>
                </View>
              ) : null}

              {showMileageInBreakdown ? (
                <View
                  style={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                  }}
                >
                  <Text type="bodyRegular">{`${mileage?.name} / ${subscriptionOptions?.[period].unit}`}</Text>

                  <Text
                    type="bodyRegular"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    {`$${formatNumber(mileageCost)}`}
                  </Text>
                </View>
              ) : null}

              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                }}
              >
                <Text type="bodyRegular">{insurance?.name}</Text>

                <Text
                  type="bodyRegular"
                  style={{ color: theme.colors.text.secondary }}
                >
                  {`$${
                    insurance
                      ? formatNumber(
                          getSubscriptionInsuranceCost(
                            insurance,
                            carCost,
                            period
                          )
                        )
                      : 0
                  }`}
                </Text>
              </View>

              {discount && (
                <View
                  style={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                  }}
                >
                  <Text type="bodyRegular" style={{ color: PINKLING }}>
                    {`Discount: ${discount.title}`}
                  </Text>

                  <Text type="bodyRegular" style={{ color: PINKLING }}>
                    {`-$${formatNumber(discount.amount)}`}
                  </Text>
                </View>
              )}
            </View>
          </View>

          {!isEVFMC || (car && car.deliveryCost > 0) ? (
            <>
              <View
                style={{
                  backgroundColor: theme.colors.background_3,
                  height: 2,
                  width: '100%',
                }}
              />

              <View style={{ padding: 16, gap: 16 }}>
                <View style={{ gap: 4 }}>
                  {!isEVFMC ? (
                    <View
                      style={{
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Text type="bodyRegular">One-Time Initiation Fee</Text>

                      <Text
                        type="bodyRegular"
                        style={{ color: theme.colors.text.secondary }}
                      >
                        {`+$${formatNumber(initiationFee)}`}
                      </Text>
                    </View>
                  ) : null}

                  {car && car?.deliveryCost > 0 && (
                    <View
                      style={{
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                      }}
                    >
                      <View
                        style={{
                          flexDirection: 'row',
                          gap: 8,
                          alignItems: 'center',
                        }}
                      >
                        <Text
                          type="bodyRegular"
                          tx="subscriptions.overviewDeliveryFee"
                        />

                        <Tooltip
                          text={t('subscriptions.overviewDeliveryFeeTooltip')}
                        />
                      </View>

                      <Text
                        type="bodyRegular"
                        style={{ color: theme.colors.text.secondary }}
                      >
                        {`+$${formatNumber(car.deliveryCost)}`}
                      </Text>
                    </View>
                  )}

                  {!isEVFMC ? (
                    <View
                      style={{
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Text type="bodyRegular">One-Time Security Deposit</Text>

                      <Text
                        type="bodyRegular"
                        style={{ color: theme.colors.text.secondary }}
                      >
                        {`$${deposit}`}
                      </Text>
                    </View>
                  ) : null}
                </View>
              </View>
            </>
          ) : null}

          <View
            style={{
              backgroundColor: theme.colors.background_3,
              height: 2,
              width: '100%',
            }}
          />

          <View style={{ padding: 16, gap: 16 }}>
            <View style={{ gap: 4 }}>
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                }}
              >
                <Text type="bodyRegular">{`${
                  period === 'weekly' ? 'Weekly' : 'Monthly'
                } Total`}</Text>

                <Text
                  type="bodyRegular"
                  style={{ color: theme.colors.text.secondary }}
                >
                  {`$${formatNumber(recurringTotal)}`}
                </Text>
              </View>

              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                }}
              >
                <Text type="bodyRegular">First Time Total</Text>

                <Text
                  type="bodyRegular"
                  style={{ color: theme.colors.text.secondary }}
                >
                  {`$${formatNumber(
                    calculatedCost + initiationFee - (discount?.amount || 0)
                  )}`}
                </Text>
              </View>
            </View>
          </View>
        </Card>

        {!isEVFMC ? (
          <Card>
            <View style={{ padding: 16 }}>
              <Text type="title2" tx="subscriptions.whenChargedTitle" />
            </View>

            <View
              style={{
                backgroundColor: theme.colors.background_3,
                height: 2,
                width: '100%',
              }}
            />

            <View style={{ padding: 16, gap: 8 }}>
              {[
                {
                  isCurrent: true,
                  label: t('subscriptions.whenChargedTodayLabel'),
                  body:
                    t('subscriptions.whenChargedToday', {
                      period: periodUnit,
                      amount: formatNumber(recurringTotal),
                    }) +
                    ` + Initiation fee ($${formatNumber(initiationFee)})` +
                    (car && car.deliveryCost > 0
                      ? ` + Delivery Fee ($${formatNumber(car.deliveryCost)})`
                      : ''),
                },
                {
                  isCurrent: false,
                  label: t('subscriptions.whenChargedDepositLabel'),
                  body: t('subscriptions.whenChargedDeposit', {
                    amount: formatNumber(deposit),
                  }),
                },
                {
                  isCurrent: false,
                  label: t('subscriptions.whenChargedRenewalLabel'),
                  body: t('subscriptions.whenChargedRenewal', {
                    period: periodUnit,
                    amount: formatNumber(recurringTotal),
                  }),
                },
              ].map((step, index) => (
                <View key={index} style={{ flexDirection: 'row', gap: 8 }}>
                  <View
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 8,
                      marginTop: 6,
                      backgroundColor: step.isCurrent
                        ? PINKLING
                        : theme.colors.text.secondary,
                    }}
                  />

                  <View style={{ flex: 1, gap: 4 }}>
                    <Text
                      type="caption1"
                      style={{
                        color: step.isCurrent
                          ? PINKLING
                          : theme.colors.text.secondary,
                      }}
                    >
                      {step.label}
                    </Text>

                    <Text type="bodyRegular">{step.body}</Text>
                  </View>
                </View>
              ))}
            </View>
          </Card>
        ) : null}

        {isEVFMC ? (
          <Card>
            <View style={{ padding: 16, gap: 16 }}>
              <Text type="title2">Charges Explained</Text>

              <View
                style={{
                  height: 1,
                  width: '100%',
                  backgroundColor: theme.colors.background_3,
                }}
              />

              <View style={{ gap: 16 }}>
                {[
                  {
                    icon: <Bolt />,
                    title:
                      'Supercharging, tolls and incidentals are billed daily',
                    body: "Usage charges aren't part of your weekly total. They're added up each day and auto-charge to your selected payment method",
                  },
                  {
                    icon: <Dollar />,
                    title: 'Your first payment covers the first week.',
                    body: 'Starting in the second week, rental fees are billed daily. Tolls, charging costs, and other incidental charges may be applied throughout the rental period.',
                  },
                  {
                    icon: <ClockBadgeCheckmark />,
                    title: 'Charged automatically every day',
                    body: 'No action needed',
                  },
                  {
                    icon: <Lock />,
                    title:
                      'If a daily payment fails and your unpaid balance goes over $50 your access to your car is locked until it\u2019s paid.',
                  },
                ].map((item, index) => (
                  <View
                    key={index}
                    style={{
                      flexDirection: 'row',
                      gap: 12,
                      alignItems: 'center',
                    }}
                  >
                    <Icon
                      type="dressed"
                      icon={item.icon}
                      fillColor={theme.colors.icon.color}
                      width={20}
                      height={20}
                    />

                    <View style={{ flex: 1, gap: 2 }}>
                      <Text type="bodyBold">{item.title}</Text>

                      {item.body ? (
                        <Text
                          type="caption1"
                          style={{ color: theme.colors.text.secondary }}
                        >
                          {item.body}
                        </Text>
                      ) : null}
                    </View>
                  </View>
                ))}
              </View>

              <View
                style={{
                  height: 1,
                  width: '100%',
                  backgroundColor: theme.colors.background_3,
                }}
              />

              <View style={{ gap: 24 }}>
                {[
                  {
                    label: 'Today',
                    isCurrent: true,
                    title: 'First week + one-time fees, paid upfront.',
                    // This card is EVFMC-only, and the initiation fee is
                    // always waived for EVFMC, so it's omitted here.
                    amount: `$${weeklyCostLabel}`,
                  },
                  {
                    label: 'First week',
                    isCurrent: false,
                    title:
                      "Week's already paid. You're only charged what you used: Tolls, charging and incidentals",
                    amount: '$0 + Tolls, charging and incidentals',
                  },
                  {
                    label: 'Starting from week 2',
                    isCurrent: false,
                    title: `One day of subscription ($${weeklyCostLabel}/7) plus that day\u2019s usage.`,
                    amount: `$${dailyCostLabel} + Tolls, charging and incidentals`,
                  },
                ].map((step, index) => (
                  <View key={index} style={{ flexDirection: 'row', gap: 8 }}>
                    <View
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: 8,
                        marginTop: 6,
                        backgroundColor: step.isCurrent
                          ? PINKLING
                          : theme.colors.text.secondary,
                      }}
                    />

                    <View style={{ flex: 1, gap: 2 }}>
                      <Text
                        type="caption1"
                        style={{ color: theme.colors.text.secondary }}
                      >
                        {step.label}
                      </Text>

                      <Text type="bodyRegular">{step.title}</Text>

                      <Text type="bodyRegular" style={{ color: PINKLING }}>
                        {step.amount}
                      </Text>
                    </View>
                  </View>
                ))}
              </View>
            </View>
          </Card>
        ) : null}

        <Card>
          <View style={{ padding: 16 }}>
            <Text type="title2">Selected Payment Method</Text>
          </View>

          <View
            style={{
              backgroundColor: theme.colors.background_3,
              height: 2,
              width: '100%',
            }}
          />

          <View ref={paymentFormViewRef} style={{ padding: 16 }}>
            <PaymentElement
              onChange={(e) => {
                setIsFormValid(e.complete);
                setPaymentMethodId(e.value?.payment_method?.id || '-1');
              }}
              options={{
                business: { name: 'Eon' },
                layout: {
                  type: 'accordion',
                  radios: true,
                  spacedAccordionItems: false,
                },
              }}
            />
          </View>
        </Card>

        <Card>
          <View
            style={{
              borderBottomColor: theme.colors.card.default.borderColor,
              borderBottomWidth: 1,
            }}
          >
            <View style={{ padding: 16 }}>
              <Text tx="checkout.digitalSignature" type="title2" />
            </View>
          </View>

          <View ref={signatureViewRef} style={{ padding: 16, gap: 16 }}>
            <Canvas
              onDone={(sig: string) => {
                setSignature(sig);
                setSignatureError(null);
              }}
              onClear={() => {
                setSignature(undefined);
              }}
              text="By signing, you agree to the terms and acknowledge the initiation fee is nonrefundable"
            />

            {signatureError ? (
              <Text type="caption1" style={{ color: theme.colors.error }}>
                {signatureError}
              </Text>
            ) : null}

            <Dialog
              header={<Text tx="checkout.termsAndConditions" type="title2" />}
              bodyJsx={
                <Text type="bodyRegular">
                  Please review our policies. By making a payment, you
                  automatically accept our terms and conditions and our{' '}
                  <Pressable
                    onPress={() => {
                      window.open(
                        'https://www.eonrides.com/legal/terms-privacy-policy',
                        '_blank'
                      );
                    }}
                    style={{ textDecorationLine: 'underline' }}
                  >
                    <Text type="bodyRegular">Privacy Policy.</Text>
                  </Pressable>
                </Text>
              }
              button={
                <Button
                  text="Terms and Conditions"
                  type="secondaryExtraSmall"
                  onPress={() => {
                    window.open(`${Env.BASE_URL}/rental-terms`, '_blank');
                  }}
                  rightIcon={
                    <Icon
                      type="naked"
                      icon={<ChevronRight />}
                      fillColor={theme.colors.button.secondary.default.text}
                      width={10}
                      height={10}
                    />
                  }
                  style={styles.reviewPoliciesButton}
                />
              }
            />
          </View>
        </Card>

        {!['xs', 'sm'].includes(UnistylesRuntime.breakpoint) && (
          <View style={{ alignItems: 'flex-end' }}>
            <Button
              tx="subscriptions.overviewButtonPay"
              values={{
                amount: formatNumber(
                  calculatedCost + initiationFee - (discount?.amount || 0)
                ),
              }}
              type="primary"
              style={{ width: 160 }}
              onPress={() => {
                mixpanelService.track('Button Clicked', {
                  'Button Name': 'Summary Pay',
                });

                handleBooking();
              }}
              loading={stripePaymentPending}
            />
          </View>
        )}

        <Modal
          animationIn="slideInUp"
          animationInTiming={300}
          animationOut="slideOutDown"
          isVisible={showPoliciesModal}
          hasBackdrop
          style={{
            margin: 0,
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onDismiss={() => {
            setShowPoliciesModal(false);
          }}
          onBackdropPress={() => {
            setShowPoliciesModal(false);
          }}
        >
          <View style={styles.policiesContainer}>
            <View
              style={{
                width: '100%',
                borderBottomWidth: 1,
                alignItems: 'center',
                borderBottomColor: theme.colors.card.default.border,
                paddingBottom: 16,
              }}
            >
              <Text type="title2">Policies</Text>

              <NavigationButton
                type={NavigationButtonTypes.cancel}
                onPress={() => {
                  setShowPoliciesModal(false);
                }}
                style={{
                  position: 'absolute',
                  right: 8,
                }}
              />
            </View>

            <ScrollView showsVerticalScrollIndicator={false}>
              <View style={{ gap: 24, maxHeight: 500 }}>
                {!isEVFMC ? (
                  <View style={{ gap: 8 }}>
                    <Text type="bodyBold">Deposit policy</Text>

                    <Text
                      type="caption1"
                      style={{ color: theme.colors.text.secondary }}
                    >
                      Before vehicle handoff, your card will be authorized for a
                      security deposit to be returned upon the safe return of
                      the vehicle at the end of your subscription. This is
                      separate from the initiation fee. The following amounts
                      will be authorized; $1000 for weekly, $1000 monthly, $1500
                      for 6+ months.
                    </Text>
                  </View>
                ) : null}

                <View style={{ gap: 8 }}>
                  <Text type="bodyBold">
                    Payment & Automatic Renewal Policy
                  </Text>

                  <Text
                    type="caption1"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    Payments are due every 7 or 30 days, based on your chosen
                    subscription period. Payment for the next period will be
                    billed 3 days before your next period start time. Your
                    subscription will automatically renew at the end of your
                    term unless canceled.
                  </Text>
                </View>

                <View style={{ gap: 8 }}>
                  <Text type="bodyBold">Vehicle Maintenance and Insurance</Text>

                  <Text
                    type="caption1"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    Regular vehicle maintenance is included in your
                    subscription. Should routine maintenance be required during
                    your rental, you may be asked to assist with coordinating
                    vehicle inspections or stops at a service center. In such
                    cases, you will be comped two prorated rental days each day
                    you participate in helping with necessary maintenance tasks.
                    For example: if at our direction, you stop by a tire shop to
                    replace the vehicle tires, we will pay for the maintenance
                    and you'll enjoy two free days in the vehicle. While
                    maintenance issues are covered by us, you are still liable
                    for non maintenance damages as may arise from accidents,
                    theft, vandalism, and negligence.
                  </Text>
                </View>

                <View style={{ gap: 8 }}>
                  <Text type="bodyBold">Cancellation policy</Text>

                  <Text
                    type="caption1"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    You can cancel your subscription at any time with a 15 day
                    notice for monthly and 6+ months subscriptions and a 7 day
                    notice for weekly subscriptions. If you're on the 6+ month
                    plan and need to end your rental before the first 6 months,
                    your pricing will revert to the monthly rate and any
                    difference in cost between monthly and 6+ months will be
                    withheld from your deposit.
                  </Text>
                </View>

                <View
                  style={{
                    backgroundColor: theme.colors.background_3,
                    height: 2,
                    width: '100%',
                  }}
                />

                <View>
                  <Text type="caption1">
                    Your subscription will consist of a renewing rental
                    agreement every 7 or 30 days - you can see a full copy of
                    this rental agreement with all the specific terms and
                    conditions below:
                  </Text>

                  <Button
                    tx="subscriptions.overviewTOC"
                    type="link"
                    onPress={() => {
                      Linking.openURL('/rental-terms').catch((err) =>
                        console.error('An error occurred', err)
                      );
                    }}
                    textStyle={{ fontSize: 12 }}
                    style={{
                      width: 120,
                      marginLeft: 0,
                      paddingTop: 0,
                      paddingRight: 0,
                      paddingBottom: 0,
                      paddingLeft: 0,
                    }}
                  />
                </View>
              </View>
            </ScrollView>
          </View>
        </Modal>
      </ScrollView>
    </View>
  );
});

const stylesheet = createStyleSheet((theme) => ({
  scrollViewContainer: {
    flex: 1,
    paddingBottom: {
      xs: 24,
      md: 48,
    },
  },
  image: {
    width: {
      xs: '100%',
      md: '100%',
    },
    height: {
      xs: 120,
      md: 150,
    },
  },
  policiesContainer: {
    width: {
      xs: '90%',
      md: 600,
    },
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.background_2,
    borderRadius: 8,
    padding: 16,
    gap: 16,
  },
  policiesTextContainer: {
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
    justifyContent: 'center',
    alignItems: {
      xs: 'flex-start',
      md: 'center',
    },
    gap: {
      xs: 16,
      md: 0,
    },
  },
  reviewPoliciesButton: {
    width: {
      xs: '60%',
      md: 170,
    },
  },
}));

export default StripeWrapper;
